import BreakpointTypeEnum from '@models/enums/BreakpointTypeEnum';
import getZoomLevel from '@utils/helpers/getZoomLevel';
import { useCallback, useEffect, useState } from 'react';

/* With the viewport breakpoint type, the width is given by the innerWidth prop on window.
This value can change when the user zooms in/out on the page, even if the screen size remains the same.
(e.g. user can be on a desktop screen size but zoom in until they get to a mobile viewport)
With the screen breakpoint type, we first compute the zoom level and then we compute the "screen" width, i.e.
that width that is not affected by the zoom, which should be equal to the innerWidth on the default 100% zoom. */
const useMediaQuery = (
  minWidth: number,
  breakpointType: BreakpointTypeEnum = BreakpointTypeEnum.Viewport
) => {
  // Set the default value to undefined in order to properly distinguish the falsey values
  // where undefined is the value before computing whether the screen size is smaller than the minWidth
  // i.e. a state where we don't really know whether we're below the threshold or not
  // and false is the value where the computation returns false
  const [isSmallerThan, setIsSmallerThan] = useState<boolean>();

  const handler = useCallback(() => {
    const zoomLevel = getZoomLevel();
    const width =
      breakpointType === BreakpointTypeEnum.Viewport
        ? window.innerWidth
        : window.innerWidth * zoomLevel;
    const isDesiredWidth = width < minWidth;
    setIsSmallerThan(isDesiredWidth);
  }, [minWidth, breakpointType]);

  useEffect(() => {
    window.addEventListener('resize', handler);
    handler();

    return () => {
      window.removeEventListener('resize', handler);
    };
  }, [handler]);

  return isSmallerThan;
};

export default useMediaQuery;
